import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";

const LOGO_URL = "/logo.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // form, success, error

  useEffect(() => {
    if (!token) {
      setStatus("error");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setStatus("success");
      toast.success("Password reset successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      toast.error(message);
      if (message.includes("expired") || message.includes("Invalid")) {
        setStatus("error");
      }
    } finally {
      setLoading(false);
    }
  };

  if (status === "success") {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="Credit2Cash" className="h-20 mx-auto logo-glow mb-8" />
          <div className="premium-card rounded-2xl p-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF66]/20 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#00FF66]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Password Reset!</h2>
            <p className="text-[#A1A1AA] mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <Link to="/login">
              <Button 
                className="w-full bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="Credit2Cash" className="h-20 mx-auto logo-glow mb-8" />
          <div className="premium-card rounded-2xl p-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#FF007F]/20 flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-[#FF007F]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Invalid or Expired Link</h2>
            <p className="text-[#A1A1AA] mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <div className="space-y-3">
              <Link to="/forgot-password">
                <Button 
                  className="w-full bg-[#FFD700] text-black font-bold hover:bg-[#FFD700]/90"
                  data-testid="request-new-link-btn"
                >
                  Request New Link
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Credit2Cash" 
            className="h-24 mx-auto logo-glow mb-4"
            data-testid="reset-password-logo"
          />
          <h1 className="text-2xl font-bold text-white">Create New Password</h1>
          <p className="text-[#A1A1AA] mt-2">Enter your new password below</p>
        </div>

        {/* Form */}
        <div className="premium-card rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="pl-10 pr-10 input-dark"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="pl-10 input-dark"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00FF66] text-black font-bold py-6 hover:bg-[#00FF66]/90 neon-glow-green"
              data-testid="reset-password-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Resetting...
                </span>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
